// Het filter "Propositie" (owner 23-10, ADR 2026-10-23-ict-los-platform).
//
// Elke pilotpropositie is één eigen sector van de run (techniek,
// economie_ondernemen, veiligheid, kinderopvang); ICT is het meetinstrument.
// Het filter kiest dus een sector, net als het filter Sector — alleen op de
// woorden van de propositie in plaats van de bedrijfstak. Beide schrijven in
// dezelfde lijst `sector`: ICT aanvinken in het ene vinkt het ook in het andere.
//
// De keuzes komen uit /api/meta `proposities` (alleen zichtbare sectoren). Met
// één keuze verschijnt het filter niet: zo verandert een verborgen geladen
// sector niets aan het scherm.

import type { MetaResponse } from "../contracts";
import { propositieNaam, verborgenIct } from "../lib/propositieFilter";
import { vastePropositie } from "../lib/sectorkeuze";
import { type FilterPil, Menu, MenuKeuzes, MenuKnopTekst, type Optie } from "../stijl";

type Propositie = NonNullable<MetaResponse["proposities"]>[number];

export const propositiesVan = (meta: MetaResponse | null | undefined): Propositie[] => meta?.proposities ?? [];

/** Toont het scherm het filter? Pas bij twee of meer zichtbare proposities. */
export const toonPropositie = (meta: MetaResponse | null | undefined): boolean => propositiesVan(meta).length > 1;

/** Het woord voor een sectorsleutel: propositienaam, anders sectorlabel, anders de sleutel. */
export function sectorWoord(meta: MetaResponse | null | undefined, key: string): string {
  const p = propositiesVan(meta).find((x) => x.sector === key);
  return (
    (p && propositieNaam(p)) ??
    meta?.sectors?.find((s) => s.key === key)?.label ??
    key
  );
}

/** Is deze sleutel een propositie die niet ook in het filter Sector staat? */
const alleenPropositie = (meta: MetaResponse | null | undefined, key: string): boolean =>
  propositiesVan(meta).some((p) => p.sector === key) && !(meta?.sectors ?? []).some((s) => s.key === key);

/** De pil van een gekozen sector: "Propositie: Techniek" of "Sector: ICT". */
export function sectorPil(meta: MetaResponse | null | undefined, key: string, weg: () => void): FilterPil {
  const soort = alleenPropositie(meta, key) ? "Propositie" : "Sector";
  return { sleutel: `s-${key}`, label: `${soort}: ${sectorWoord(meta, key)}`, weg };
}

/** De sectoren die het filter Sector toont: zonder de pilotsectoren (die staan onder Propositie). */
export const bedrijfstakken = (meta: MetaResponse | null | undefined) =>
  (meta?.sectors ?? [])
    .filter((s) => !alleenPropositie(meta, s.key) && !verborgenIct(meta, s.key))
    .map((s) => (meta?.interneSectoren?.includes(s.key) ? { ...s, label: `${s.label} (intern)` } : s));

export function PropositieMenu({
  meta,
  gekozen,
  onWissel,
  tellingen,
}: {
  meta: MetaResponse | null | undefined;
  gekozen: readonly string[];
  onWissel: (sector: string) => void;
  tellingen?: Record<string, number>;
}) {
  // Een vaste propositie (accountmanager, business partner) kent geen menu.
  if (vastePropositie() || !toonPropositie(meta)) return null;
  const opties: Optie[] = propositiesVan(meta).map((p) => ({
    waarde: p.sector,
    label: propositieNaam(p),
    ...(tellingen ? { tel: tellingen[p.sector] ?? 0 } : {}),
  }));
  const aantal = gekozen.filter((g) => opties.some((o) => o.waarde === g)).length;
  return (
    <Menu label="Propositie" aan={aantal > 0} inhoud={<MenuKnopTekst label="Propositie" tel={aantal} />}>
      <MenuKeuzes kop="Propositie" opties={opties} gekozen={gekozen} onWissel={onWissel} />
    </Menu>
  );
}
